import { useState } from "react";
import { authStore } from "../auth";
import { Button } from "../components/Button";
import { useFlow } from "../flow/FlowContext";
import { useT } from "../i18n";

export function AccountScreen() {
  const { answers, setAccount, next, canContinue } = useFlow();
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const session = authStore.getSession();
  const showSession =
    Boolean(session) && !session!.email.endsWith("@guest.un8.local");

  const continueWithAuth = async () => {
    setError(null);
    setBusy(true);
    try {
      const result = await authStore.ensureRegistered(answers.email, answers.password);
      if (!result.ok) {
        setError(result.error);
        return;
      }
      if ("recoveryCode" in result && result.recoveryCode) {
        setRecoveryCode(result.recoveryCode);
        return;
      }
      next();
    } finally {
      setBusy(false);
    }
  };

  if (recoveryCode) {
    return (
      <>
        <div className="prompt screen-enter">
          <header className="screen-head">
            <div className="screen-head__eyebrow">{t("account.eyebrow")}</div>
            <h1 className="screen-head__title">{t("account.recoveryTitle")}</h1>
            <p className="screen-head__text">{t("account.recoveryText")}</p>
          </header>
          <p
            className="field__input"
            style={{ fontFamily: "ui-monospace, monospace", letterSpacing: "0.06em", textAlign: "center" }}
          >
            {recoveryCode}
          </p>
        </div>
        <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
          <Button
            onClick={() => {
              setRecoveryCode(null);
              next();
            }}
          >
            {t("account.savedContinue")}
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="prompt screen-enter">
        <header className="screen-head">
          <div className="screen-head__eyebrow">{t("account.eyebrow")}</div>
          <h1 className="screen-head__title">{t("account.title")}</h1>
          <p className="screen-head__text">{t("account.text")}</p>
        </header>
        <div className="stack">
          {showSession ? (
            <p className="field__meta">
              {t("account.signedInBody", { email: session!.email, userId: session!.userId })}
            </p>
          ) : null}
          <label className="field">
            <span className="sr-only">{t("account.handle")}</span>
            <input
              className="field__input"
              type="text"
              autoComplete="username"
              placeholder={t("account.handlePlaceholder")}
              value={answers.email}
              onChange={(e) => setAccount(e.target.value, answers.password)}
            />
          </label>
          <label className="field">
            <span className="sr-only">{t("account.passphrase")}</span>
            <input
              className="field__input"
              type="password"
              autoComplete="new-password"
              placeholder={t("account.passphrasePlaceholder")}
              value={answers.password}
              onChange={(e) => setAccount(answers.email, e.target.value)}
            />
          </label>
          {error ? <p className="capture__error">{error}</p> : null}
        </div>
      </div>
      <div className="shell__footer" style={{ paddingLeft: 0, paddingRight: 0 }}>
        <Button onClick={() => void continueWithAuth()} disabled={!canContinue || busy}>
          {busy ? t("common.saving") : t("common.next")}
        </Button>
      </div>
    </>
  );
}
